/**
 * SOLID Review
 * S — Strong. One concern: taking a seat — by a table code, by hosting, by the server's word on a cold return — each
 *     built from the snapshot as a `ClientSession`, asked, and answered through a `ClientDecision`. The rules, the
 *     route a code leads by included, are the aggregate's.
 * O — Good. A new way to a seat is a plan and an answer here, rows in the table, and their rules on the aggregate.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the seating ops' table and nothing else.
 * D — Strong. The session aggregate, the decision builder and the schemas.
 */
import { ClientSession } from '../../session/ClientSession';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type {
  ICommandJoinRouted, ICommandTableJoined, ICommandTableRejoined, ICommandTableLateJoined, ICommandTableHosted,
  ICommandActiveRoomFound, IEntityJoinRoute, IEntityProceed, IEntityRoundRead, IEntityColdRoom, IPayloadCodeLookup,
  IPayloadHost, IPayloadPlayer,
} from '../../ports/schemas/SessionMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TSeatingOp =
  | 'planJoin' | 'joinRouted' | 'tableJoined' | 'tableRejoined' | 'tableLateJoined' | 'planHost' | 'tableHosted'
  | 'planColdRejoin' | 'activeRoomFound';

/**
 * TAKING A SEAT (frontend-domain-oo P5, 2026-10-26; handbook §28.272) — the decide functions of
 * `decisions/session/seating.ts` (FE2: useSmartJoinRouting, MainMenuPanel's auto-host, useColdLoginRejoin) as a
 * service. A seat taken writes the table through the session, which holds it.
 */
export class Seating implements IServiceDecisionTable<TSeatingOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TSeatingOp> = {
    planJoin: (input) => this.planJoin(input),
    joinRouted: (input, command) => this.joinRouted(input, command),
    tableJoined: (input, command) => this.tableJoined(input, command),
    tableRejoined: (input, command) => this.tableRejoined(input, command),
    tableLateJoined: (input, command) => this.tableLateJoined(input, command),
    planHost: (input) => this.planHost(input),
    tableHosted: (input, command) => this.tableHosted(input, command),
    planColdRejoin: (input) => this.planColdRejoin(input),
    activeRoomFound: (input, command) => this.activeRoomFound(input, command),
  };

  planJoin(input: IPropsClientInput): TClientDecision<IPayloadCodeLookup> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planJoin());
  }

  joinRouted(input: IPropsClientInput, command: ICommandJoinRouted): TClientDecision<IEntityJoinRoute> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.joinRouted(command));
  }

  tableJoined(input: IPropsClientInput, command: ICommandTableJoined): TClientDecision<IEntityProceed> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityProceed = { proceed: session.tableJoined(command) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  tableRejoined(input: IPropsClientInput, command: ICommandTableRejoined): TClientDecision<IEntityRoundRead> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.tableRejoined(command));
  }

  tableLateJoined(input: IPropsClientInput, command: ICommandTableLateJoined): TClientDecision<IEntityRoundRead> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityRoundRead = { read: session.tableLateJoined(command) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  planHost(input: IPropsClientInput): TClientDecision<IPayloadHost> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planHost());
  }

  tableHosted(input: IPropsClientInput, command: ICommandTableHosted): TClientDecision<IEntityProceed> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityProceed = { proceed: session.tableHosted(command) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  planColdRejoin(input: IPropsClientInput): TClientDecision<IPayloadPlayer> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planColdRejoin());
  }

  activeRoomFound(input: IPropsClientInput, command: ICommandActiveRoomFound): TClientDecision<IEntityColdRoom> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityColdRoom = { record: session.activeRoomFound(command) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }
}
